// 국양건축사사무소 30주년 축하 모달 (2026-06-19 하루 한정).
// 설정/동작 정책은 config.ts 의 ANNIVERSARY 주석 참조.
//   - 날짜 게이트 + ENABLED 플래그 + "다시 보지 않기"(localStorage) 3중 가드.
//   - 사건 수는 읽기 전용 집계 1회. 실패해도 모달은 정상 표시(숫자만 생략).

import { useEffect, useState } from 'react';
import dayjs from 'dayjs';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { keyframes } from '@mui/system';

import { ANNIVERSARY } from 'config';
import { countAppraisals } from 'firebase/appraisals-service';
import { fireConfetti } from 'components/AnniversaryConfetti';

const float = keyframes`
  0%   { transform: translateY(0) rotate(0deg); opacity: 0.7; }
  50%  { transform: translateY(-10px) rotate(8deg); opacity: 1; }
  100% { transform: translateY(0) rotate(0deg); opacity: 0.7; }
`;

// 콘텐츠 등장 — 살짝 튀어오르며 페이드인
const popIn = keyframes`
  0%   { transform: scale(0.6) translateY(8px); opacity: 0; }
  60%  { transform: scale(1.06); opacity: 1; }
  100% { transform: scale(1) translateY(0); opacity: 1; }
`;

// 배지 글로우 맥동
const pulseGlow = keyframes`
  0%, 100% { box-shadow: 0 8px 24px ${'#C8A24B'}66; }
  50%      { box-shadow: 0 8px 34px ${'#E0533D'}88, 0 0 14px ${'#3D7BE0'}55; }
`;

// 그라데이션 텍스트 흐르기
const shimmer = keyframes`
  0%   { background-position: 0% center; }
  100% { background-position: 200% center; }
`;

const GOLD = '#C8A24B';
const GOLD_DEEP = '#9C7A2E';

// 축제 팔레트 — 띠/반짝임/텍스트에 공유
const FESTIVE = ['#C8A24B', '#E0533D', '#3D7BE0', '#3DAE6B', '#E07FB0', '#8B5CF6'];
const SPARKLES = ['✦', '✧', '★', '✦', '✧', '❋'];

// 오늘이 이벤트 날이고, 켜져 있고, "다시 보지 않기"를 누른 적 없는지.
// (날짜가 오늘이면 새 로그인/새로고침마다 다시 뜬다 — "고맙습니다"는 기록을 남기지 않음)
function shouldShow(): boolean {
  if (!ANNIVERSARY.ENABLED) return false;
  if (dayjs().format('YYYY-MM-DD') !== ANNIVERSARY.DATE) return false;
  try {
    return localStorage.getItem(ANNIVERSARY.STORAGE_KEY) !== '1';
  } catch {
    return true;
  }
}

export default function AnniversaryModal() {
  const [open, setOpen] = useState<boolean>(() => shouldShow());
  const [count, setCount] = useState<number | null>(null);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    countAppraisals()
      .then((n) => alive && setCount(n))
      .catch(() => {
        /* 집계 실패 시 숫자만 생략 — 모달은 그대로 */
      });
    return () => {
      alive = false;
    };
  }, [open]);

  // 그냥 닫기 — 기록을 남기지 않으므로 다음 로그인/새로고침에 다시 뜬다.
  // 닫는 순간 꽃가루 버스트 발사(모달에 가려 있던 축포가 이때 팡 터진다).
  const handleClose = () => {
    setOpen(false);
    fireConfetti();
  };

  // "다시 보지 않기" — 이 브라우저에서 더 이상 띄우지 않도록 기억
  const handleDismissForever = () => {
    try {
      localStorage.setItem(ANNIVERSARY.STORAGE_KEY, '1');
    } catch {
      /* localStorage 불가 환경이어도 닫기는 동작 */
    }
    setOpen(false);
    fireConfetti();
  };

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            overflow: 'hidden',
            borderRadius: 3,
            position: 'relative',
            border: `1px solid ${GOLD}55`,
            background: 'linear-gradient(180deg, #FFFDF6 0%, #FFFFFF 60%)'
          }
        }
      }}
    >
      {/* 상단 골드 띠 */}
      <Box sx={{ height: 6, background: GOLD }} />

      {/* 떠다니는 알록달록 반짝임 */}
      {SPARKLES.map((s, i) => (
        <Box
          key={i}
          aria-hidden
          sx={{
            position: 'absolute',
            top: 12 + (i % 3) * 10,
            left: `${8 + i * 15}%`,
            color: FESTIVE[i % FESTIVE.length],
            fontSize: 12 + (i % 3) * 5,
            animation: `${float} ${2.4 + (i % 4) * 0.5}s ease-in-out infinite`,
            animationDelay: `${i * 0.3}s`,
            pointerEvents: 'none'
          }}
        >
          {s}
        </Box>
      ))}

      <Stack
        spacing={2.5}
        alignItems="center"
        sx={{ px: 4, pt: 4.5, pb: 1.5, textAlign: 'center', animation: `${popIn} 0.5s ease-out both` }}
      >
        {/* 30th 배지 — 등장 후 글로우 맥동 */}
        <Box
          sx={{
            width: 96,
            height: 96,
            borderRadius: '50%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            background: `conic-gradient(from 210deg, ${GOLD}, ${GOLD_DEEP}, ${GOLD})`,
            animation: `${popIn} 0.6s ease-out both, ${pulseGlow} 2.6s ease-in-out 0.6s infinite`
          }}
        >
          <Typography sx={{ fontSize: 34, fontWeight: 800, lineHeight: 1 }}>30</Typography>
          <Typography sx={{ fontSize: 12, letterSpacing: 2, opacity: 0.95 }}>YEARS</Typography>
        </Box>

        <Stack spacing={0.5}>
          <Typography sx={{ color: GOLD_DEEP, fontWeight: 700, letterSpacing: 1 }}>{ANNIVERSARY.OFFICE}</Typography>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 800,
              backgroundImage: `linear-gradient(90deg, ${FESTIVE.join(', ')}, ${FESTIVE[0]})`,
              backgroundSize: '200% auto',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              color: 'transparent',
              animation: `${shimmer} 4s linear infinite`
            }}
          >
            창립 {ANNIVERSARY.YEARS}주년을 축하합니다🎉
          </Typography>
        </Stack>

        <Typography sx={{ whiteSpace: 'pre-line', color: 'text.secondary', fontSize: 15, lineHeight: 1.7 }}>
          {ANNIVERSARY.MESSAGE}
        </Typography>

        {count != null && count > 0 && (
          <Box
            sx={{
              mt: 0.5,
              px: 2,
              py: 1.25,
              borderRadius: 2,
              width: '100%',
              bgcolor: `${GOLD}14`,
              border: `1px dashed ${GOLD}66`
            }}
          >
            <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>그동안 이 시스템과 함께한 사건</Typography>
            <Typography sx={{ fontSize: 22, fontWeight: 800, color: GOLD_DEEP }}>{count.toLocaleString('ko-KR')}건</Typography>
          </Box>
        )}
      </Stack>

      <Box sx={{ px: 4, pb: 3.5, pt: 1 }}>
        <Button
          fullWidth
          variant="contained"
          onClick={handleClose}
          sx={{
            py: 1.1,
            fontWeight: 700,
            color: '#fff',
            bgcolor: GOLD_DEEP,
            transition: 'transform 0.15s ease, box-shadow 0.15s ease',
            boxShadow: `0 6px 18px ${GOLD}55`,
            '&:hover': { bgcolor: GOLD_DEEP, transform: 'translateY(-1px)', boxShadow: `0 10px 24px ${GOLD}77` }
          }}
        >
          고맙습니다 🙏
        </Button>
        <Typography
          component="button"
          onClick={handleDismissForever}
          sx={{
            display: 'block',
            mx: 'auto',
            mt: 1.25,
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            color: 'text.disabled',
            fontSize: 12,
            '&:hover': { textDecoration: 'underline' }
          }}
        >
          다시 보지 않기
        </Typography>
      </Box>
    </Dialog>
  );
}
